/**
 * @file        job-values.js
 *              Utility code related to encoding or decode client-supplied values that are used in the
 *              worker (or vice-versa), such as elements of the input set, output set, and work function
 *              arguments.
 * @author      Wes Garland, wes@kingsds.network
 * @date        Feb 2022
 */
'use strict';

const { DcpURL } = require('dcp/common/dcp-url');
const debugging = require('dcp/debugging').scope('dcp-client');
const KVINv8 = require('kvin-v8');
const KVINv9 = require('kvin');
const wallet = require('dcp/dcp-client/wallet');
const userCtors = {
  [ wallet.Address.name ]: wallet.Address,
  DcpURL
};
Object.assign(KVINv8.userCtors, userCtors);
Object.assign(KVINv9.userCtors, userCtors);

/**
 * Determine the serializer a job requires.
 */
exports.getSerializerForJob = function getSerializerForJob(job, capabilities)
{
  if (!(job.needCapabilities & capabilities.KVINv9))
    return KVINv8;
  return KVINv9;
}

/**
 * Represent a single datum at a "remote" location.
 *
 * @constructor RemoteValue
 * @extends DcpURL
 */
exports.RemoteValue = function remoteDataSet$$RemoteValue()
{
  DcpURL.apply(this, arguments);
}
exports.RemoteValue.prototype = new DcpURL('http://remote.value.dcp/prototype');

/**
 * Serialize a job value, detecting the correct serialization form and MIME type; 
 * JSON and KVIN selected as appropriate.
 *
 * @param   {any} value - The value to serialize
 * @param   {any} serializer - The serializer to use if JSON isn't acceptable
 * @returns {{string: string, method:string, MIMEType: string }} - with three properties:
 *             string   - serialization of value
 *             method   - how value was serialized (e.g. 'kvin', 'json')
 *             MIMEType - MIME information corresponding to method (e.g. 'application/kvin', 'application/json')
 */
exports.serializeJobValue = function jobValues$$serializeJobValue(value, serializer)
{
  var string;
  var methodFn;

  if (DcpURL.isURL(value))
    return value;

  switch (typeof value) {
    default:
      methodFn = serializer;
      break;
    case 'string':
    case 'boolean':
      methodFn = JSON;
      break;
    case 'number':
      if (!isNaN(value) && value !== Infinity && value !== -Infinity && Object.is(value, -0)) // https://eslint.org/docs/latest/rules/no-compare-neg-zero
        methodFn = JSON;
      else
        methodFn = serializer;
      break;
    case 'object':
      if (typeof value.toJSON === 'function')
        methodFn = JSON;
      else
        methodFn = serializer;
      break;
  }

  string = methodFn.stringify(value);

  const method = methodFn === JSON ? 'json' : 'kvin';
  const MIMEType = exports.mimeTypes_bySerializer[method];
  debugging('dcp-client') && console.log('serializeJobValue:', string.length, method, MIMEType);
  return { string, method, MIMEType };
}

exports.mimeTypes_bySerializer = {
  'kvin': 'application/x-kvin',
  'json': 'application/json',
};
